<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import AiBotAPI from 'dashboard/api/aiBot';
import { platformIntlLocale } from 'dashboard/helper/platformLocale';
import { safeRenewalCheckoutURL } from 'dashboard/helper/platformPolicyRules';
import {
  aiUsageEventLabelKey,
  normalizeAiCreditSnapshot,
  normalizeAiUsagePeriod,
} from 'dashboard/helper/aiBotUsage';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const TABS = [
  'settings',
  'knowledge',
  'documents',
  'inboxes',
  'tools',
  'usage',
];

const STATUS_LABELS = {
  pending: 'AI_BOT.KNOWLEDGE.PENDING',
  approved: 'AI_BOT.KNOWLEDGE.APPROVED',
  rejected: 'AI_BOT.KNOWLEDGE.REJECTED',
};

const DOCUMENT_STATUS_LABELS = {
  draft: 'AI_BOT.DOCUMENTS.DRAFT',
  queued: 'AI_BOT.DOCUMENTS.QUEUED',
  processing: 'AI_BOT.DOCUMENTS.PROCESSING',
  ready: 'AI_BOT.DOCUMENTS.READY',
  failed: 'AI_BOT.DOCUMENTS.FAILED',
};

const ASSISTANT_PAGE_SIZE = 8;
const USAGE_PAGE_SIZE = 10;
const CREDIT_PAGE_SIZE = 10;
const USAGE_PERIODS = [7, 30, 90];
const CREDIT_CHECKOUT_CONTEXT_TTL_MS = 30 * 60 * 1000;
const CREDIT_PAYMENT_STATES = new Set([
  'success',
  'return',
  'pending',
  'cancelled',
  'canceled',
  'error',
  'failed',
]);
const CREDIT_ACTION_REASON_KEYS = {
  unlimited: 'UNLIMITED',
  no_packages: 'NO_PACKAGES',
  exchange_rate_unavailable: 'EXCHANGE_RATE_UNAVAILABLE',
  payment_setup_required: 'PAYMENT_SETUP_REQUIRED',
  payment_gateway_unavailable: 'PAYMENT_GATEWAY_UNAVAILABLE',
};
const CREDIT_PURCHASE_STATUS_KEYS = {
  pending: 'PENDING',
  paid: 'PAID',
  failed: 'FAILED',
  cancelled: 'CANCELLED',
  canceled: 'CANCELLED',
  refunded: 'REFUNDED',
};
const DEFAULT_TENANT_LIMITS = {
  max_assistants: 2,
  max_documents: 20,
  max_document_mb: 2,
  max_storage_mb: 100,
  max_document_chars: 120000,
  max_knowledge_entries: 200,
  max_tools: 15,
  assistant_name_chars: 80,
  assistant_icon_chars: 16,
  assistant_product_name_chars: 80,
  assistant_description_chars: 600,
  assistant_handoff_message_chars: 500,
  assistant_guardrails_lines: 20,
  assistant_guardrail_chars: 200,
  assistant_guidelines_lines: 20,
  assistant_guideline_chars: 200,
  knowledge_question_chars: 300,
  knowledge_answer_chars: 2000,
  document_name_chars: 120,
  document_url_chars: 2000,
  tool_name_chars: 80,
  tool_slug_chars: 64,
  tool_description_chars: 800,
  tool_parameters: 20,
  tool_parameter_name_chars: 64,
  tool_parameter_description_chars: 200,
  tool_template_chars: 4000,
  tool_response_template_chars: 2000,
  tool_auth_field_chars: 120,
};

const UNLIMITED_LIMIT_KEYS = new Set([
  'max_assistants',
  'max_documents',
  'max_document_mb',
  'max_storage_mb',
  'max_knowledge_entries',
  'max_tools',
]);

const ASSISTANT_ICON_OPTIONS = [
  { value: 'sparkles', icon: 'i-lucide-sparkles', labelKey: 'AI_BOT.ICONS.SPARKLES' },
  { value: 'bot', icon: 'i-lucide-bot', labelKey: 'AI_BOT.ICONS.BOT' },
  { value: 'headset', icon: 'i-lucide-headset', labelKey: 'AI_BOT.ICONS.HEADSET' },
  { value: 'shopping-bag', icon: 'i-lucide-shopping-bag', labelKey: 'AI_BOT.ICONS.SHOPPING_BAG' },
  { value: 'book-open', icon: 'i-lucide-book-open', labelKey: 'AI_BOT.ICONS.BOOK_OPEN' },
  { value: 'wrench', icon: 'i-lucide-wrench', labelKey: 'AI_BOT.ICONS.WRENCH' },
];

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');

const queryValue = value =>
  Array.isArray(value) ? String(value[0] || '') : String(value || '');
const positivePage = value => {
  const parsed = Number.parseInt(queryValue(value), 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : 1;
};
const requestedTab = queryValue(route.query.tab);

const assistants = ref([]);
const selectedAssistantId = ref(null);
const activeTab = ref(TABS.includes(requestedTab) ? requestedTab : 'settings');
const assistantPage = ref(1);
const usagePage = ref(positivePage(route.query.usage_page));
const usageDays = ref(normalizeAiUsagePeriod(route.query.usage_days));
const usageAssistantId = ref(queryValue(route.query.usage_assistant_id));
const creditPage = ref(positivePage(route.query.credit_page));
const isFetching = ref(false);
const isSaving = ref(false);
const isDeleting = ref(false);
const isKnowledgeLoading = ref(false);
const isDocumentLoading = ref(false);
const isInboxLoading = ref(false);
const isToolLoading = ref(false);
const isUsageLoading = ref(false);
const isCreditLoading = ref(false);
const checkoutAmountCents = ref(0);
const topUpAmount = ref('');
const hasMounted = ref(false);
let latestCreditRequest = 0;
let latestUsageRequest = 0;

const knowledgeEntries = ref([]);
const documents = ref([]);
const inboxLinks = ref([]);
const customTools = ref([]);
const usageSummary = ref(null);
const creditManagement = ref(null);
const creditError = ref(false);
const creditCheckoutError = ref(false);
const tenantLimits = ref({ ...DEFAULT_TENANT_LIMITS });
const tenantCounts = ref({
  assistants: 0,
  documents: 0,
  knowledge_entries: 0,
  tools: 0,
});
const selectedInboxId = ref('');
const selectedToolId = ref(null);

const assistantForm = ref({});
const guardrailsText = ref('');
const guidelinesText = ref('');
const newKnowledge = ref({ question: '', answer: '', status: 'approved' });
const newDocument = ref({
  name: '',
  source_type: 'text',
  source_url: '',
  content: '',
  process_now: true,
});
const toolForm = ref({
  name: '',
  slug: '',
  description: '',
  http_method: 'get',
  endpoint_url: '',
  enabled: true,
  request_template: '',
  response_template: '',
  auth_config: {
    type: 'none',
    header_name: '',
    token: '',
    username: '',
    password: '',
  },
});
const toolParameters = ref([]);
const responseFieldsText = ref('');
const contactIdentitySources = ['contact_id', 'contact_email', 'contact_phone'];

const selectedAssistant = computed(() =>
  assistants.value.find(assistant => assistant.id === selectedAssistantId.value)
);

const availableInboxes = computed(() => {
  const linkedIds = inboxLinks.value.map(link => Number(link.inbox_id));
  return (inboxes.value || []).filter(
    inbox => !linkedIds.includes(Number(inbox.id))
  );
});

const assistantCount = computed(() => assistants.value.length);
const assistantTotalPages = computed(() =>
  Math.max(1, Math.ceil(assistantCount.value / ASSISTANT_PAGE_SIZE))
);
const paginatedAssistants = computed(() => {
  const start = (assistantPage.value - 1) * ASSISTANT_PAGE_SIZE;
  return assistants.value.slice(start, start + ASSISTANT_PAGE_SIZE);
});
const selectedTool = computed(() =>
  customTools.value.find(tool => tool.id === selectedToolId.value)
);
const intlLocale = computed(() => platformIntlLocale(locale.value));
const normalizedCurrency = value => {
  const currency = String(value || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) return 'USD';

  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(0);
    return currency;
  } catch {
    return 'USD';
  }
};
const activeBudget = computed(() => usageSummary.value?.current_account_budget || null);
const activeBudgetSnapshot = computed(() =>
  normalizeAiCreditSnapshot(activeBudget.value)
);
const usagePeriodOptions = computed(() =>
  USAGE_PERIODS.map(days => ({
    value: days,
    label: t('AI_BOT.USAGE.PERIOD_DAYS', { days: formatNumber(days) }),
  }))
);
const usageAssistantOptions = computed(() => [
  { value: '', label: t('AI_BOT.USAGE.ALL_ASSISTANTS') },
  ...assistants.value.map(assistant => ({
    value: String(assistant.id),
    label: assistant.name,
  })),
]);
const usagePagination = computed(
  () =>
    usageSummary.value?.pagination || {
      page: usagePage.value,
      pages: 1,
      total: 0,
    }
);
const usageTotalPages = computed(() =>
  Math.max(1, Number(usagePagination.value.pages || 1))
);
const creditCurrency = computed(() =>
  normalizedCurrency(
    creditManagement.value?.credit_currency || creditManagement.value?.currency
  )
);
const paymentCurrency = computed(() =>
  normalizedCurrency(creditManagement.value?.payment_currency)
);
const creditPurchases = computed(() =>
  asArray(creditManagement.value?.purchases?.records)
);
const creditPurchasePagination = computed(
  () =>
    creditManagement.value?.purchases || {
      page: creditPage.value,
      page_size: CREDIT_PAGE_SIZE,
      total: 0,
    }
);
const creditTotalPages = computed(() =>
  Math.max(
    1,
    Math.ceil(
      Number(creditPurchasePagination.value.total || 0) /
        Math.max(1, Number(creditPurchasePagination.value.page_size || CREDIT_PAGE_SIZE))
    )
  )
);
const creditSnapshot = computed(() =>
  normalizeAiCreditSnapshot(creditManagement.value)
);
const creditIsUnlimited = computed(
  () => Boolean(creditManagement.value) && creditSnapshot.value.unlimited
);
const creditPaymentState = computed(() => {
  const state = queryValue(route.query.credit_payment).toLowerCase();
  return CREDIT_PAYMENT_STATES.has(state) ? state : '';
});
const creditPaymentNotice = computed(() => {
  if (['success', 'return'].includes(creditPaymentState.value)) {
    return t('AI_BOT.CREDITS.PAYMENT.RETURNED');
  }
  if (creditPaymentState.value === 'pending') {
    return t('AI_BOT.CREDITS.PAYMENT.PENDING');
  }
  if (['cancelled', 'canceled'].includes(creditPaymentState.value)) {
    return t('AI_BOT.CREDITS.PAYMENT.CANCELLED');
  }
  if (['error', 'failed'].includes(creditPaymentState.value)) {
    return t('AI_BOT.CREDITS.PAYMENT.ERROR');
  }
  return '';
});
const creditActionMessage = computed(() => {
  const reason = String(creditManagement.value?.action?.reason || '');
  const key = CREDIT_ACTION_REASON_KEYS[reason] || 'UNAVAILABLE';
  return t(`AI_BOT.CREDITS.ACTION.${key}`);
});
const centsFromMoneyInput = value => {
  const normalized = String(value || '').trim().replace(',', '.');
  const match = normalized.match(/^(0|[1-9]\d{0,7})(?:\.(\d{0,2}))?$/);
  if (!match) return 0;

  const cents = Number(match[1]) * 100 + Number((match[2] || '').padEnd(2, '0'));
  return Number.isSafeInteger(cents) ? cents : 0;
};
const moneyInputFromCents = cents => {
  const amount = Number(cents || 0);
  if (!Number.isSafeInteger(amount) || amount <= 0) return '';

  return (amount / 100).toFixed(2).replace(/\.00$/, '');
};
const requestedTopUpCents = computed(() => centsFromMoneyInput(topUpAmount.value));
const pendingTopUpCents = computed(() =>
  Number(creditManagement.value?.pending_top_up_cents || 0)
);
const topUpAmountValid = computed(() => {
  const amount = requestedTopUpCents.value;
  const minimum = Number(creditManagement.value?.min_top_up_cents || 0);
  const maximum = Number(creditManagement.value?.max_top_up_cents || 0);
  return amount >= minimum && amount <= maximum;
});
const estimatedTopUpCreditCents = computed(() => {
  const rate = Number(creditManagement.value?.effective_rate_micros || 0);
  if (!topUpAmountValid.value || !Number.isSafeInteger(rate) || rate <= 0) return 0;

  return Math.floor((requestedTopUpCents.value * 1000000) / rate);
});
const canStartTopUp = computed(
  () =>
    !creditIsUnlimited.value &&
    creditManagement.value?.action?.available === true &&
    topUpAmountValid.value &&
    estimatedTopUpCreditCents.value > 0 &&
    !checkoutAmountCents.value &&
    (!pendingTopUpCents.value || pendingTopUpCents.value === requestedTopUpCents.value)
);
const assistantRemaining = computed(() => {
  if (isUnlimitedLimit('max_assistants')) return Number.POSITIVE_INFINITY;

  return Math.max(0, limitFor('max_assistants') - tenantCounts.value.assistants);
});
const canCreateAssistant = computed(
  () => Boolean(selectedAssistantId.value) || assistantRemaining.value > 0
);
const selectedAssistantToolCount = computed(() => customTools.value.length);
const selectedAssistantToolLimitReached = computed(() =>
  hasReachedLimitValue(selectedAssistantToolCount.value, 'max_tools')
);

const tabItems = computed(() =>
  TABS.map(tab => ({
    id: tab,
    label: t(`AI_BOT.TABS.${tab.toUpperCase()}`),
  }))
);

const asArray = value => (Array.isArray(value) ? value : []);

const asObject = value =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const formatNumber = value =>
  Number(value || 0).toLocaleString(intlLocale.value);

const limitFor = key =>
  Number(tenantLimits.value?.[key] ?? DEFAULT_TENANT_LIMITS[key] ?? 0);

const countFor = key => Number(tenantCounts.value?.[key] || 0);

const isUnlimitedLimit = key =>
  UNLIMITED_LIMIT_KEYS.has(key) && limitFor(key) <= 0;

const hasReachedLimit = (countKey, limitKey) =>
  hasReachedLimitValue(countFor(countKey), limitKey);

const hasReachedLimitValue = (count, limitKey) =>
  !isUnlimitedLimit(limitKey) && Number(count || 0) >= limitFor(limitKey);

const limitLabel = key =>
  isUnlimitedLimit(key) ? t('AI_BOT.LIMITS.UNLIMITED') : formatNumber(limitFor(key));

const iconFor = icon =>
  ASSISTANT_ICON_OPTIONS.find(option => option.value === icon)?.icon ||
  'i-lucide-sparkles';

const limitCounter = (value, key) =>
  t('AI_BOT.LIMITS.COUNTER', {
    current: String(value || '').length,
    max: formatNumber(limitFor(key)),
  });

const lineCounter = (value, linesKey) =>
  t('AI_BOT.LIMITS.LINES', {
    current: fromLines(value || '').length,
    max: formatNumber(limitFor(linesKey)),
  });

const usageCounter = (countKey, limitKey) =>
  t('AI_BOT.LIMITS.USAGE', {
    current: formatNumber(countFor(countKey)),
    max: limitLabel(limitKey),
  });

const usageCounterForValue = (current, limitKey) =>
  t('AI_BOT.LIMITS.USAGE', {
    current: formatNumber(current),
    max: limitLabel(limitKey),
  });

const formatMegabytes = bytes => (Number(bytes || 0) / 1024 / 1024).toFixed(2);

const documentStorageLabel = computed(() => {
  const used = formatMegabytes(countFor('document_storage_bytes'));
  const storageLimit = limitFor('max_storage_mb');
  if (storageLimit <= 0) {
    return t('AI_BOT.DOCUMENTS.STORAGE_UNLIMITED', { used });
  }

  return t('AI_BOT.DOCUMENTS.STORAGE_USAGE', {
    used,
    mb: formatNumber(storageLimit),
  });
});

const documentFetchLimitLabel = computed(() => {
  if (isUnlimitedLimit('max_document_mb')) {
    return t('AI_BOT.DOCUMENTS.MAX_FILE_SIZE_UNLIMITED');
  }

  return t('AI_BOT.DOCUMENTS.MAX_FILE_SIZE', {
    mb: formatNumber(limitFor('max_document_mb')),
  });
});

const formatMoneyMicros = value =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'currency',
    currency: creditCurrency.value,
    maximumFractionDigits: 4,
  }).format(Number(value || 0) / 1000000);

const formatUsdMicros = value =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  }).format(Number(value || 0) / 1000000);

const formatMoneyCents = (value, currency = creditCurrency.value) =>
  new Intl.NumberFormat(intlLocale.value, {
    style: 'currency',
    currency: normalizedCurrency(currency),
    maximumFractionDigits: 2,
  }).format(Number(value || 0) / 100);

const creditLimitLabel = value =>
  creditIsUnlimited.value && Number(value || 0) <= 0
    ? t('AI_BOT.CREDITS.UNLIMITED_VALUE')
    : formatMoneyCents(value);

const creditRemainingLabel = computed(() =>
  creditIsUnlimited.value
    ? t('AI_BOT.CREDITS.UNLIMITED_VALUE')
    : formatMoneyMicros(creditSnapshot.value.remainingMicros)
);

const usageEventLabel = eventType =>
  t(`AI_BOT.USAGE.EVENTS.${aiUsageEventLabelKey(eventType)}`);

const formatUsageDate = value => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return t('AI_BOT.USAGE.DATE_UNAVAILABLE');

  return new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatCreditDate = value => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) {
    return t('AI_BOT.CREDITS.PURCHASES.NOT_PAID');
  }

  return new Intl.DateTimeFormat(intlLocale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const creditPurchaseStatus = value => {
  const key = CREDIT_PURCHASE_STATUS_KEYS[String(value || '').toLowerCase()] || 'UNKNOWN';
  return t(`AI_BOT.CREDITS.PURCHASES.STATUS.${key}`);
};

const budgetText = budget => {
  if (!budget) return t('AI_BOT.USAGE.UNLIMITED');
  const snapshot = normalizeAiCreditSnapshot(budget);
  if (snapshot.unlimited) return t('AI_BOT.USAGE.UNLIMITED');

  return t('AI_BOT.USAGE.BUDGET_VALUE', {
    remaining: formatUsdMicros(snapshot.remainingMicros),
  });
};

const apiErrorMessage = (error, fallbackKey) => {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string') {
    if (/<html|<!doctype|<style|something went wrong/i.test(responseData)) {
      return t(fallbackKey);
    }

    return (
      responseData
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 240) || t(fallbackKey)
    );
  }

  return responseData?.error || responseData?.message || t(fallbackKey);
};

const toLines = value =>
  Array.isArray(value) ? value.filter(Boolean).join('\n') : '';

const fromLines = value =>
  value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

const newToolParameter = parameter => ({
  name: parameter?.name || '',
  description: parameter?.description || '',
  required: parameter?.required ?? true,
  type: parameter?.type || 'string',
  max_length: parameter?.max_length || 500,
  source: parameter?.source || 'model',
  response_field: parameter?.response_field || '',
});

const addToolParameter = () => {
  toolParameters.value.push(newToolParameter());
};

const removeToolParameter = index => {
  toolParameters.value.splice(index, 1);
};

const toolRunsAutomatically = computed(() => {
  const sources = toolParameters.value.map(parameter => parameter.source);
  return (
    toolForm.value.http_method === 'get' &&
    fromLines(responseFieldsText.value).length > 0 &&
    sources.length > 0 &&
    !sources.includes('model') &&
    toolParameters.value.some(
      parameter =>
        contactIdentitySources.includes(parameter.source) &&
        parameter.response_field.trim().length > 0
    )
  );
});

const applyAssistantExample = type => {
  const examples = {
    ecommerce: {
      icon: 'shopping-bag',
      name: t('AI_BOT.EXAMPLES.ASSISTANT_ECOMMERCE_NAME'),
      product_name: t('AI_BOT.EXAMPLES.ASSISTANT_ECOMMERCE_PRODUCT'),
      description: t('AI_BOT.EXAMPLES.ASSISTANT_ECOMMERCE_DESCRIPTION'),
      guardrails: t('AI_BOT.EXAMPLES.ASSISTANT_ECOMMERCE_GUARDRAILS'),
      guidelines: t('AI_BOT.EXAMPLES.ASSISTANT_ECOMMERCE_GUIDELINES'),
    },
    support: {
      icon: 'headset',
      name: t('AI_BOT.EXAMPLES.ASSISTANT_SUPPORT_NAME'),
      product_name: t('AI_BOT.EXAMPLES.ASSISTANT_SUPPORT_PRODUCT'),
      description: t('AI_BOT.EXAMPLES.ASSISTANT_SUPPORT_DESCRIPTION'),
      guardrails: t('AI_BOT.EXAMPLES.ASSISTANT_SUPPORT_GUARDRAILS'),
      guidelines: t('AI_BOT.EXAMPLES.ASSISTANT_SUPPORT_GUIDELINES'),
    },
  };
  const example = examples[type];
  if (!example) return;

  Object.assign(assistantForm.value, {
    icon: example.icon,
    name: example.name,
    product_name: example.product_name,
    description: example.description,
  });
  guardrailsText.value = example.guardrails;
  guidelinesText.value = example.guidelines;
};

const applyKnowledgeExample = () => {
  newKnowledge.value = {
    question: t('AI_BOT.EXAMPLES.KNOWLEDGE_QUESTION'),
    answer: t('AI_BOT.EXAMPLES.KNOWLEDGE_ANSWER'),
    status: 'approved',
  };
};

const applyToolExample = type => {
  const examples = {
    order: {
      name: t('AI_BOT.EXAMPLES.TOOL_ORDER_NAME'),
      slug: 'order_status',
      description: t('AI_BOT.EXAMPLES.TOOL_ORDER_DESCRIPTION'),
      http_method: 'get',
      endpoint_url: 'https://api.example.com/orders',
      parameters: [newToolParameter({ name: 'phone', description: 'Customer phone number', source: 'contact_phone', response_field: 'customer.phone' })],
      response_fields: 'order.status\norder.eta',
      request_template: '',
      response_template: t('AI_BOT.EXAMPLES.TOOL_ORDER_RESPONSE'),
    },
    booking: {
      name: t('AI_BOT.EXAMPLES.TOOL_BOOKING_NAME'),
      slug: 'booking_lookup',
      description: t('AI_BOT.EXAMPLES.TOOL_BOOKING_DESCRIPTION'),
      http_method: 'post',
      endpoint_url: 'https://api.example.com/bookings/search',
      parameters: [
        newToolParameter({ name: 'phone', description: 'Customer phone number', source: 'contact_phone', response_field: 'customer.phone' }),
        newToolParameter({ name: 'email', description: 'Customer email address', source: 'contact_email', response_field: 'customer.email', required: false }),
      ],
      response_fields: 'booking.status\nbooking.starts_at',
      request_template: '{ "phone": "{{phone}}", "email": "{{email}}" }',
      response_template: t('AI_BOT.EXAMPLES.TOOL_BOOKING_RESPONSE'),
    },
  };
  const example = examples[type];
  if (!example) return;

  Object.assign(toolForm.value, {
    name: example.name,
    slug: example.slug,
    description: example.description,
    http_method: example.http_method,
    endpoint_url: example.endpoint_url,
    request_template: example.request_template,
    response_template: example.response_template,
    enabled: true,
  });
  toolParameters.value = example.parameters;
  responseFieldsText.value = example.response_fields;
};

const resetAssistantForm = assistant => {
  assistantForm.value = {
    id: assistant?.id,
    icon: assistant?.icon || 'sparkles',
    name: assistant?.name || t('AI_BOT.DEFAULT_ASSISTANT_NAME'),
    description:
      assistant?.description || t('AI_BOT.DEFAULT_ASSISTANT_DESCRIPTION'),
    product_name: assistant?.product_name || '',
    handoff_message: assistant?.handoff_message || '',
    enabled: assistant?.enabled ?? true,
    capture_memories: assistant?.capture_memories ?? true,
    suggest_faqs: assistant?.suggest_faqs ?? true,
    auto_resolve_enabled: assistant?.auto_resolve_enabled ?? false,
  };
  guardrailsText.value = toLines(assistant?.guardrails || []);
  guidelinesText.value = toLines(assistant?.response_guidelines || []);
};

const resetKnowledgeForm = () => {
  newKnowledge.value = { question: '', answer: '', status: 'approved' };
};

const resetDocumentForm = () => {
  newDocument.value = {
    name: '',
    source_type: 'text',
    source_url: '',
    content: '',
    process_now: true,
  };
};

const resetToolForm = tool => {
  selectedToolId.value = tool?.id || null;
  toolForm.value = {
    name: tool?.name || '',
    slug: tool?.slug || '',
    description: tool?.description || '',
    http_method: tool?.http_method || 'get',
    endpoint_url: tool?.endpoint_url || '',
    enabled: tool?.enabled ?? true,
    request_template: tool?.request_template || '',
    response_template: tool?.response_template || '',
    auth_config: {
      type: tool?.auth_config?.type || 'none',
      header_name: tool?.auth_config?.header_name || '',
      token: '',
      username: tool?.auth_config?.username || '',
      password: '',
    },
  };
  toolParameters.value = (tool?.parameters || []).map(newToolParameter);
  responseFieldsText.value = toLines(tool?.response_fields || []);
};

const selectAssistant = assistant => {
  selectedAssistantId.value = assistant?.id || null;
  resetAssistantForm(assistant);
};

const resetAssistantSelection = () => {
  selectedAssistantId.value = null;
  knowledgeEntries.value = [];
  documents.value = [];
  inboxLinks.value = [];
  customTools.value = [];
  resetAssistantForm();
};

const startNewAssistant = () => {
  if (hasReachedLimit('assistants', 'max_assistants')) {
    useAlert(t('AI_BOT.LIMITS.ASSISTANT_LIMIT_REACHED'));
    return;
  }

  resetAssistantSelection();
  activeTab.value = 'settings';
};

const applyLimitPayload = payload => {
  const meta = payload?.meta || (Array.isArray(payload) ? {} : payload || {});
  const limits = meta.limits || payload?.limits;
  const counts = meta.counts || payload?.counts;

  if (limits) {
    tenantLimits.value = {
      ...DEFAULT_TENANT_LIMITS,
      ...limits,
    };
  }

  if (!counts) return;

  tenantCounts.value = {
    assistants: 0,
    documents: 0,
    document_storage_bytes: 0,
    knowledge_entries: 0,
    tools: 0,
    ...counts,
  };
};

const fetchTenantLimits = async () => {
  try {
    const { data } = await AiBotAPI.limits();
    applyLimitPayload(data);
  } catch {
    tenantLimits.value = { ...DEFAULT_TENANT_LIMITS };
  }
};

const fetchAssistants = async () => {
  isFetching.value = true;
  try {
    const { data } = await AiBotAPI.assistants();
    applyLimitPayload(data);
    assistants.value = Array.isArray(data) ? asArray(data) : asArray(data.data);
    assistantPage.value = Math.min(assistantPage.value, assistantTotalPages.value);
    if (
      usageAssistantId.value &&
      !assistants.value.some(
        assistant => String(assistant.id) === usageAssistantId.value
      )
    ) {
      usageAssistantId.value = '';
    }
    selectAssistant(assistants.value[0]);
    if (!assistants.value.length) resetAssistantSelection();
  } catch (error) {
    assistants.value = [];
    resetAssistantSelection();
    useAlert(apiErrorMessage(error, 'AI_BOT.API.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const fetchKnowledge = async assistantId => {
  if (!assistantId) {
    knowledgeEntries.value = [];
    return;
  }

  isKnowledgeLoading.value = true;
  try {
    const { data } = await AiBotAPI.knowledgeEntries(assistantId, {
      limit: 100,
    });
    knowledgeEntries.value = asArray(data);
  } catch (error) {
    knowledgeEntries.value = [];
    useAlert(apiErrorMessage(error, 'AI_BOT.API.LOAD_ERROR'));
  } finally {
    isKnowledgeLoading.value = false;
  }
};

const fetchDocuments = async assistantId => {
  if (!assistantId) {
    documents.value = [];
    return;
  }

  isDocumentLoading.value = true;
  try {
    const { data } = await AiBotAPI.documents(assistantId, { limit: 100 });
    if (assistantId === selectedAssistantId.value) documents.value = asArray(data);
  } catch (error) {
    documents.value = [];
    useAlert(apiErrorMessage(error, 'AI_BOT.API.LOAD_ERROR'));
  } finally {
    isDocumentLoading.value = false;
  }
};

const fetchInboxLinks = async assistantId => {
  if (!assistantId) {
    inboxLinks.value = [];
    return;
  }

  isInboxLoading.value = true;
  try {
    const { data } = await AiBotAPI.inboxLinks(assistantId);
    inboxLinks.value = asArray(data);
  } catch (error) {
    inboxLinks.value = [];
    useAlert(apiErrorMessage(error, 'AI_BOT.API.LOAD_ERROR'));
  } finally {
    isInboxLoading.value = false;
  }
};

const fetchTools = async assistantId => {
  if (!assistantId) {
    customTools.value = [];
    return;
  }

  isToolLoading.value = true;
  try {
    const { data } = await AiBotAPI.customTools(assistantId, { limit: 100 });
    customTools.value = asArray(data);
    resetToolForm(customTools.value[0]);
  } catch (error) {
    customTools.value = [];
    resetToolForm();
    useAlert(apiErrorMessage(error, 'AI_BOT.API.LOAD_ERROR'));
  } finally {
    isToolLoading.value = false;
  }
};

const fetchUsage = async () => {
  const requestId = ++latestUsageRequest;
  isUsageLoading.value = true;
  try {
    const { data } = await AiBotAPI.usage({
      assistant_id: usageAssistantId.value || undefined,
      days: usageDays.value,
      page: usagePage.value,
      page_size: USAGE_PAGE_SIZE,
    });
    if (requestId !== latestUsageRequest) return;

    usageSummary.value = asObject(data);
    const responsePage = positivePage(usageSummary.value?.pagination?.page);
    const boundedPage = Math.min(responsePage, usageTotalPages.value);
    if (boundedPage !== usagePage.value) {
      usagePage.value = boundedPage;
      await syncRouteState();
    }
  } catch (error) {
    if (requestId !== latestUsageRequest) return;

    usageSummary.value = null;
    useAlert(apiErrorMessage(error, 'AI_BOT.API.LOAD_ERROR'));
  } finally {
    if (requestId === latestUsageRequest) isUsageLoading.value = false;
  }
};

const syncRouteState = () => {
  const tab = activeTab.value;
  const creditPageQuery = String(creditPage.value);
  const usagePageQuery = String(usagePage.value);
  const usageDaysQuery = String(usageDays.value);
  const usageAssistantQuery = usageAssistantId.value;
  if (
    queryValue(route.query.tab) === tab &&
    queryValue(route.query.credit_page) === creditPageQuery &&
    queryValue(route.query.usage_page) === usagePageQuery &&
    queryValue(route.query.usage_days) === usageDaysQuery &&
    queryValue(route.query.usage_assistant_id) === usageAssistantQuery
  ) {
    return Promise.resolve();
  }

  const synchronizedQuery = {
    ...route.query,
    tab,
    credit_page: creditPageQuery,
    usage_page: usagePageQuery,
    usage_days: usageDaysQuery,
  };
  if (usageAssistantQuery) {
    synchronizedQuery.usage_assistant_id = usageAssistantQuery;
  } else {
    delete synchronizedQuery.usage_assistant_id;
  }

  return router.replace({
    query: synchronizedQuery,
  });
};

const applyCreditManagement = payload => {
  creditManagement.value = asObject(payload);
  const pendingAmount = Number(creditManagement.value?.pending_top_up_cents || 0);
  if (pendingAmount > 0 && !checkoutAmountCents.value) {
    topUpAmount.value = moneyInputFromCents(pendingAmount);
  }
  const responsePage = positivePage(creditManagement.value?.purchases?.page);
  const boundedPage = Math.min(responsePage, creditTotalPages.value);
  if (boundedPage !== creditPage.value) creditPage.value = boundedPage;
};

const fetchCreditManagement = async () => {
  if (activeTab.value !== 'usage') return;

  const requestId = ++latestCreditRequest;
  isCreditLoading.value = true;
  creditError.value = false;
  try {
    const response = await AiBotAPI.creditManagement({
      page: creditPage.value,
      page_size: CREDIT_PAGE_SIZE,
    });
    if (requestId !== latestCreditRequest) return;
    applyCreditManagement(response.data);
  } catch {
    if (requestId !== latestCreditRequest) return;
    creditManagement.value = null;
    creditError.value = true;
  } finally {
    if (requestId === latestCreditRequest) isCreditLoading.value = false;
  }
};

const refreshUsageTab = () =>
  Promise.all([fetchUsage(), fetchCreditManagement()]);

const creditCheckoutStorageKey = () =>
  `taco.ai-credit-checkout.${queryValue(route.params.accountId)}`;

const clearCreditCheckoutContext = () => {
  try {
    window.sessionStorage.removeItem(creditCheckoutStorageKey());
  } catch (error) {
    if (!(error instanceof DOMException)) throw error;
  }
};

const readCreditCheckoutContext = () => {
  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(creditCheckoutStorageKey()) || 'null'
    );
    if (
      !stored ||
      typeof stored !== 'object' ||
      Date.now() - Number(stored.startedAt || 0) > CREDIT_CHECKOUT_CONTEXT_TTL_MS
    ) {
      clearCreditCheckoutContext();
      return null;
    }
    return stored;
  } catch (error) {
    if (error instanceof SyntaxError || error instanceof DOMException) {
      clearCreditCheckoutContext();
      return null;
    }
    throw error;
  }
};

const writeCreditCheckoutContext = amountCents => {
  const routeContext = {
    tab: 'usage',
    credit_page: String(creditPage.value),
    usage_page: String(usagePage.value),
    usage_days: String(usageDays.value),
  };
  if (/^\d+$/.test(usageAssistantId.value)) {
    routeContext.usage_assistant_id = usageAssistantId.value;
  }

  try {
    window.sessionStorage.setItem(
      creditCheckoutStorageKey(),
      JSON.stringify({
        amountCents: Number(amountCents),
        startedAt: Date.now(),
        routeContext,
      })
    );
  } catch (error) {
    if (!(error instanceof DOMException)) throw error;
  }
};

const restoreCreditCheckoutContext = async () => {
  const stored = readCreditCheckoutContext();
  if (!stored) return;

  if (!creditPaymentState.value) {
    topUpAmount.value = moneyInputFromCents(stored.amountCents);
    clearCreditCheckoutContext();
    return;
  }

  await router.replace({
    query: {
      ...asObject(stored.routeContext),
      ...route.query,
      tab: 'usage',
    },
  });
  clearCreditCheckoutContext();
};

const startCreditCheckout = async () => {
  if (!canStartTopUp.value) return;

  const amountCents = requestedTopUpCents.value;
  checkoutAmountCents.value = amountCents;
  creditCheckoutError.value = false;
  try {
    await syncRouteState();
    writeCreditCheckoutContext(amountCents);
    const response = await AiBotAPI.creditCheckout(amountCents);
    const checkoutURL = safeRenewalCheckoutURL(response.data?.url);
    if (!checkoutURL) throw new Error('invalid_credit_checkout_url');

    window.location.assign(checkoutURL);
  } catch {
    clearCreditCheckoutContext();
    checkoutAmountCents.value = 0;
    creditCheckoutError.value = true;
  }
};

const changeUsagePage = async delta => {
  const nextPage = Math.min(
    usageTotalPages.value,
    Math.max(1, usagePage.value + delta)
  );
  if (nextPage === usagePage.value) return;

  usagePage.value = nextPage;
  await syncRouteState();
  fetchUsage();
};

const applyUsageFilters = async () => {
  usagePage.value = 1;
  await syncRouteState();
  if (activeTab.value === 'usage') fetchUsage();
};

const changeUsageAssistant = async requestedAssistantId => {
  usageAssistantId.value = String(requestedAssistantId || '');
  await applyUsageFilters();
};

const changeUsagePeriod = async requestedDays => {
  usageDays.value = normalizeAiUsagePeriod(requestedDays);
  await applyUsageFilters();
};

const changeCreditPage = delta => {
  const nextPage = Math.min(
    creditTotalPages.value,
    Math.max(1, creditPage.value + delta)
  );
  if (nextPage === creditPage.value) return;

  creditPage.value = nextPage;
};

const assistantPayload = () => ({
  ...assistantForm.value,
  guardrails: fromLines(guardrailsText.value),
  response_guidelines: fromLines(guidelinesText.value),
});

const saveAssistant = async () => {
  isSaving.value = true;
  try {
    const payload = assistantPayload();
    const { data } = selectedAssistantId.value
      ? await AiBotAPI.updateAssistant(selectedAssistantId.value, payload)
      : await AiBotAPI.createAssistant(payload);

    const index = assistants.value.findIndex(
      assistant => assistant.id === data.id
    );
    if (index >= 0) assistants.value.splice(index, 1, data);
    else {
      assistants.value.unshift(data);
      assistantPage.value = 1;
    }

    selectAssistant(data);
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteAssistant = async () => {
  if (!selectedAssistantId.value) return;

  isDeleting.value = true;
  try {
    await AiBotAPI.deleteAssistant(selectedAssistantId.value);
    assistants.value = assistants.value.filter(
      assistant => assistant.id !== selectedAssistantId.value
    );
    assistantPage.value = Math.min(assistantPage.value, assistantTotalPages.value);
    selectAssistant(assistants.value[0]);
    if (!assistants.value.length) resetAssistantSelection();
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
  }
};

const createKnowledge = async () => {
  if (!selectedAssistantId.value) return;

  isKnowledgeLoading.value = true;
  try {
    const { data } = await AiBotAPI.createKnowledgeEntry(
      selectedAssistantId.value,
      newKnowledge.value
    );
    knowledgeEntries.value.unshift(data);
    resetKnowledgeForm();
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.KNOWLEDGE_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.KNOWLEDGE_ERROR'));
  } finally {
    isKnowledgeLoading.value = false;
  }
};

const approveKnowledge = async entry => {
  isKnowledgeLoading.value = true;
  try {
    const { data } = await AiBotAPI.approveKnowledgeEntry(
      selectedAssistantId.value,
      entry.id
    );
    Object.assign(entry, data);
    useAlert(t('AI_BOT.API.KNOWLEDGE_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.KNOWLEDGE_ERROR'));
  } finally {
    isKnowledgeLoading.value = false;
  }
};

const rejectKnowledge = async entry => {
  isKnowledgeLoading.value = true;
  try {
    const { data } = await AiBotAPI.rejectKnowledgeEntry(
      selectedAssistantId.value,
      entry.id
    );
    Object.assign(entry, data);
    useAlert(t('AI_BOT.API.KNOWLEDGE_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.KNOWLEDGE_ERROR'));
  } finally {
    isKnowledgeLoading.value = false;
  }
};

const deleteKnowledge = async entry => {
  isKnowledgeLoading.value = true;
  try {
    await AiBotAPI.deleteKnowledgeEntry(selectedAssistantId.value, entry.id);
    knowledgeEntries.value = knowledgeEntries.value.filter(
      item => item.id !== entry.id
    );
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.KNOWLEDGE_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.KNOWLEDGE_ERROR'));
  } finally {
    isKnowledgeLoading.value = false;
  }
};

const createDocument = async () => {
  if (!selectedAssistantId.value) return;

  isDocumentLoading.value = true;
  try {
    const { data } = await AiBotAPI.createDocument(
      selectedAssistantId.value,
      newDocument.value
    );
    documents.value.unshift(data);
    resetDocumentForm();
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.DOCUMENT_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.DOCUMENT_ERROR'));
  } finally {
    isDocumentLoading.value = false;
  }
};

const processDocument = async document => {
  isDocumentLoading.value = true;
  try {
    const { data } = await AiBotAPI.processDocument(
      selectedAssistantId.value,
      document.id
    );
    Object.assign(document, data);
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.DOCUMENT_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.DOCUMENT_ERROR'));
  } finally {
    isDocumentLoading.value = false;
  }
};

const deleteDocument = async document => {
  isDocumentLoading.value = true;
  try {
    await AiBotAPI.deleteDocument(selectedAssistantId.value, document.id);
    documents.value = documents.value.filter(item => item.id !== document.id);
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.DOCUMENT_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.DOCUMENT_ERROR'));
  } finally {
    isDocumentLoading.value = false;
  }
};

const linkInbox = async () => {
  if (!selectedAssistantId.value || !selectedInboxId.value) return;

  isInboxLoading.value = true;
  try {
    const { data } = await AiBotAPI.createInboxLink(selectedAssistantId.value, {
      inbox_id: selectedInboxId.value,
      status: 'active',
    });
    inboxLinks.value.unshift(data);
    selectedInboxId.value = '';
    useAlert(t('AI_BOT.API.INBOX_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.INBOX_ERROR'));
  } finally {
    isInboxLoading.value = false;
  }
};

const toggleInboxLink = async link => {
  isInboxLoading.value = true;
  try {
    const nextStatus = link.status === 'active' ? 'inactive' : 'active';
    const { data } = await AiBotAPI.updateInboxLink(
      selectedAssistantId.value,
      link.id,
      { status: nextStatus }
    );
    Object.assign(link, data);
    useAlert(t('AI_BOT.API.INBOX_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.INBOX_ERROR'));
  } finally {
    isInboxLoading.value = false;
  }
};

const unlinkInbox = async link => {
  isInboxLoading.value = true;
  try {
    await AiBotAPI.deleteInboxLink(selectedAssistantId.value, link.id);
    inboxLinks.value = inboxLinks.value.filter(item => item.id !== link.id);
    useAlert(t('AI_BOT.API.INBOX_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.INBOX_ERROR'));
  } finally {
    isInboxLoading.value = false;
  }
};

const saveTool = async () => {
  if (!selectedAssistantId.value) return;

  isToolLoading.value = true;
  try {
    const payload = {
      ...toolForm.value,
      parameters: toolParameters.value,
      response_fields: fromLines(responseFieldsText.value),
    };
    const { data } = selectedToolId.value
      ? await AiBotAPI.updateCustomTool(
          selectedAssistantId.value,
          selectedToolId.value,
          payload
        )
      : await AiBotAPI.createCustomTool(selectedAssistantId.value, payload);

    const index = customTools.value.findIndex(tool => tool.id === data.id);
    if (index >= 0) customTools.value.splice(index, 1, data);
    else customTools.value.unshift(data);
    resetToolForm(data);
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.TOOL_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.TOOL_ERROR'));
  } finally {
    isToolLoading.value = false;
  }
};

const deleteTool = async () => {
  if (!selectedToolId.value) return;

  isToolLoading.value = true;
  try {
    await AiBotAPI.deleteCustomTool(selectedAssistantId.value, selectedToolId.value);
    customTools.value = customTools.value.filter(
      tool => tool.id !== selectedToolId.value
    );
    resetToolForm(customTools.value[0]);
    await fetchTenantLimits();
    useAlert(t('AI_BOT.API.TOOL_SUCCESS'));
  } catch (error) {
    useAlert(apiErrorMessage(error, 'AI_BOT.API.TOOL_ERROR'));
  } finally {
    isToolLoading.value = false;
  }
};

watch(selectedAssistantId, assistantId => {
  fetchKnowledge(assistantId);
  fetchDocuments(assistantId);
  fetchInboxLinks(assistantId);
  fetchTools(assistantId);
});

const documentPoll = window.setInterval(() => {
  if (
    activeTab.value === 'documents' &&
    !isDocumentLoading.value &&
    documents.value.some(document => ['queued', 'processing'].includes(document.status))
  ) {
    fetchDocuments(selectedAssistantId.value);
  }
}, 3000);
onUnmounted(() => window.clearInterval(documentPoll));

watch(activeTab, tab => {
  if (!hasMounted.value) return;

  syncRouteState();
  if (tab === 'usage') refreshUsageTab();
});

watch(creditPage, () => {
  if (!hasMounted.value) return;

  syncRouteState();
  if (activeTab.value === 'usage') fetchCreditManagement();
});

watch(
  () => route.query.tab,
  value => {
    const tab = queryValue(value);
    if (TABS.includes(tab) && tab !== activeTab.value) activeTab.value = tab;
  }
);

watch(
  () => route.query.credit_page,
  value => {
    const page = positivePage(value);
    if (page !== creditPage.value) creditPage.value = page;
  }
);

watch(
  () => route.query.usage_page,
  value => {
    const page = positivePage(value);
    if (page === usagePage.value) return;

    usagePage.value = page;
    if (hasMounted.value && activeTab.value === 'usage') fetchUsage();
  }
);

watch(
  () => route.query.usage_days,
  value => {
    const days = normalizeAiUsagePeriod(value);
    if (days === usageDays.value) return;

    usageDays.value = days;
    if (hasMounted.value && activeTab.value === 'usage') fetchUsage();
  }
);

watch(
  () => route.query.usage_assistant_id,
  value => {
    const assistantId = queryValue(value);
    if (assistantId !== usageAssistantId.value) {
      usageAssistantId.value = assistantId;
      if (hasMounted.value && activeTab.value === 'usage') fetchUsage();
    }
  }
);

onMounted(async () => {
  await restoreCreditCheckoutContext();
  await syncRouteState();
  store.dispatch('inboxes/get');
  await fetchTenantLimits();
  await fetchAssistants();
  await syncRouteState();
  hasMounted.value = true;
  if (activeTab.value === 'usage') refreshUsageTab();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="t('AI_BOT.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('AI_BOT.HEADER')"
        :description="t('AI_BOT.DESCRIPTION')"
      >
        <template #count>
          <span class="text-body-main text-n-slate-11">
            {{ t('AI_BOT.COUNT', { n: assistantCount }) }}
          </span>
        </template>
        <template #actions>
          <Button
            icon="i-lucide-plus"
            size="sm"
            :label="t('AI_BOT.NEW_ASSISTANT')"
            :disabled="hasReachedLimit('assistants', 'max_assistants')"
            @click="startNewAssistant"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="grid w-full grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside class="flex min-w-0 flex-col gap-2">
          <div class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-xs text-n-slate-10">
            {{ usageCounter('assistants', 'max_assistants') }}
          </div>
          <button
            v-for="assistant in paginatedAssistants"
            :key="assistant.id"
            type="button"
            class="flex min-h-16 w-full items-center gap-3 rounded-lg border px-4 py-3 text-start transition-colors"
            :class="
              assistant.id === selectedAssistantId
                ? 'border-n-blue-7 bg-n-blue-2 text-n-slate-12'
                : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
            "
            @click="selectAssistant(assistant)"
          >
            <span
              class="flex size-9 flex-shrink-0 items-center justify-center rounded-md bg-n-alpha-2 text-n-slate-11"
            >
              <span :class="iconFor(assistant.icon)" class="size-4" />
            </span>
            <span class="min-w-0">
              <span class="block truncate text-sm font-medium">
                {{ assistant.name }}
              </span>
              <span class="block truncate text-xs text-n-slate-10">
                {{ assistant.description }}
              </span>
            </span>
            <span
              class="h-2.5 w-2.5 flex-shrink-0 rounded-full"
              :class="assistant.enabled ? 'bg-n-teal-9' : 'bg-n-slate-7'"
            />
          </button>
          <div
            v-if="assistantTotalPages > 1"
            class="flex items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-xs text-n-slate-10"
          >
            <button
              type="button"
              class="rounded-md px-2 py-1 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="assistantPage <= 1"
              @click="assistantPage -= 1"
            >
              {{ t('AI_BOT.PAGINATION.PREVIOUS') }}
            </button>
            <span>{{ t('AI_BOT.PAGINATION.PAGE', { page: assistantPage, pages: assistantTotalPages }) }}</span>
            <button
              type="button"
              class="rounded-md px-2 py-1 hover:bg-n-alpha-2 disabled:opacity-40"
              :disabled="assistantPage >= assistantTotalPages"
              @click="assistantPage += 1"
            >
              {{ t('AI_BOT.PAGINATION.NEXT') }}
            </button>
          </div>
          <div
            v-if="!assistants.length"
            class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
          >
            {{ t('AI_BOT.NO_ASSISTANTS') }}
          </div>
        </aside>

        <section class="min-w-0">
          <div class="mb-5 flex flex-wrap gap-2">
            <button
              v-for="tab in tabItems"
              :key="tab.id"
              type="button"
              class="min-h-11 rounded-lg px-4 text-sm font-medium"
              :class="
                activeTab === tab.id
                  ? 'bg-n-slate-12 text-n-slate-1'
                  : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
              "
              @click="activeTab = tab.id"
            >
              {{ tab.label }}
            </button>
          </div>

          <div
            v-if="activeTab === 'settings'"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-5"
          >
            <div class="mb-5 flex flex-wrap items-center gap-2">
              <span class="text-xs font-medium uppercase text-n-slate-10">
                {{ t('AI_BOT.EXAMPLES.TITLE') }}
              </span>
              <button
                type="button"
                class="rounded-md bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-3"
                @click="applyAssistantExample('ecommerce')"
              >
                {{ t('AI_BOT.EXAMPLES.ASSISTANT_ECOMMERCE') }}
              </button>
              <button
                type="button"
                class="rounded-md bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-3"
                @click="applyAssistantExample('support')"
              >
                {{ t('AI_BOT.EXAMPLES.ASSISTANT_SUPPORT') }}
              </button>
            </div>
            <div class="grid gap-4 md:grid-cols-2">
              <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                <span>{{ t('AI_BOT.FORM.ICON') }}</span>
                <select
                  v-model="assistantForm.icon"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                >
                  <option
                    v-for="option in ASSISTANT_ICON_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ t(option.labelKey) }}
                  </option>
                </select>
              </label>
              <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                <span>{{ t('AI_BOT.FORM.NAME') }}</span>
                <input
                  v-model="assistantForm.name"
                  :maxlength="limitFor('assistant_name_chars')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <span class="text-xs text-n-slate-10">
                  {{ limitCounter(assistantForm.name, 'assistant_name_chars') }}
                </span>
              </label>
              <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                <span>{{ t('AI_BOT.FORM.PRODUCT_NAME') }}</span>
                <input
                  v-model="assistantForm.product_name"
                  :maxlength="limitFor('assistant_product_name_chars')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <span class="text-xs text-n-slate-10">
                  {{ limitCounter(assistantForm.product_name, 'assistant_product_name_chars') }}
                </span>
              </label>
              <label
                class="flex flex-col gap-1.5 text-sm text-n-slate-12 md:col-span-2"
              >
                <span>{{ t('AI_BOT.FORM.DESCRIPTION') }}</span>
                <textarea
                  v-model="assistantForm.description"
                  :maxlength="limitFor('assistant_description_chars')"
                  rows="2"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8"
                />
                <span class="text-xs text-n-slate-10">
                  {{ limitCounter(assistantForm.description, 'assistant_description_chars') }}
                </span>
              </label>
              <label
                class="flex flex-col gap-1.5 text-sm text-n-slate-12 md:col-span-2"
              >
                <span>{{ t('AI_BOT.FORM.HANDOFF_MESSAGE') }}</span>
                <textarea
                  v-model="assistantForm.handoff_message"
                  :maxlength="limitFor('assistant_handoff_message_chars')"
                  rows="2"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8"
                />
                <span class="text-xs text-n-slate-10">
                  {{ limitCounter(assistantForm.handoff_message, 'assistant_handoff_message_chars') }}
                </span>
              </label>
              <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                <span>{{ t('AI_BOT.FORM.GUARDRAILS') }}</span>
                <textarea
                  v-model="guardrailsText"
                  rows="5"
                  :placeholder="t('AI_BOT.FORM.GUARDRAILS_PLACEHOLDER')"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8"
                />
                <span class="text-xs text-n-slate-10">
                  {{ lineCounter(guardrailsText, 'assistant_guardrails_lines') }}
                </span>
              </label>
              <label class="flex flex-col gap-1.5 text-sm text-n-slate-12">
                <span>{{ t('AI_BOT.FORM.GUIDELINES') }}</span>
                <textarea
                  v-model="guidelinesText"
                  rows="5"
                  :placeholder="t('AI_BOT.FORM.GUIDELINES_PLACEHOLDER')"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8"
                />
                <span class="text-xs text-n-slate-10">
                  {{ lineCounter(guidelinesText, 'assistant_guidelines_lines') }}
                </span>
              </label>
            </div>

            <div class="mt-5 grid gap-3 md:grid-cols-2">
              <label class="flex items-center gap-3 text-sm text-n-slate-12">
                <input v-model="assistantForm.enabled" type="checkbox" />
                <span>{{ t('AI_BOT.FORM.ENABLED') }}</span>
              </label>
              <label class="flex items-center gap-3 text-sm text-n-slate-12">
                <input v-model="assistantForm.capture_memories" type="checkbox" />
                <span>{{ t('AI_BOT.FORM.CAPTURE_MEMORIES') }}</span>
              </label>
              <label class="flex items-center gap-3 text-sm text-n-slate-12">
                <input v-model="assistantForm.suggest_faqs" type="checkbox" />
                <span>{{ t('AI_BOT.FORM.SUGGEST_FAQS') }}</span>
              </label>
              <label class="flex items-center gap-3 text-sm text-n-slate-12">
                <input
                  v-model="assistantForm.auto_resolve_enabled"
                  type="checkbox"
                />
                <span>{{ t('AI_BOT.FORM.AUTO_RESOLVE') }}</span>
              </label>
            </div>

            <div class="mt-6 flex flex-wrap justify-end gap-3">
              <Button
                v-if="selectedAssistantId"
                icon="i-lucide-trash-2"
                ruby
                faded
                :label="t('AI_BOT.DELETE_ASSISTANT')"
                :is-loading="isDeleting"
                @click="deleteAssistant"
              />
              <Button
                icon="i-lucide-save"
                :label="
                  selectedAssistantId
                    ? t('AI_BOT.SAVE_ASSISTANT')
                    : t('AI_BOT.CREATE_ASSISTANT')
                "
                :is-loading="isSaving"
                :disabled="!canCreateAssistant"
                @click="saveAssistant"
              />
            </div>
          </div>

          <div
            v-else-if="activeTab === 'knowledge'"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-5"
          >
            <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div class="text-xs text-n-slate-10">
                {{ usageCounter('knowledge_entries', 'max_knowledge_entries') }}
              </div>
              <button
                type="button"
                class="rounded-md bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-3"
                @click="applyKnowledgeExample"
              >
                {{ t('AI_BOT.EXAMPLES.KNOWLEDGE') }}
              </button>
            </div>
            <div class="mb-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_10rem]">
              <input
                v-model="newKnowledge.question"
                :placeholder="t('AI_BOT.KNOWLEDGE.QUESTION')"
                :maxlength="limitFor('knowledge_question_chars')"
                class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
              />
              <select
                v-model="newKnowledge.status"
                class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
              >
                <option value="approved">
                  {{ t('AI_BOT.KNOWLEDGE.APPROVED') }}
                </option>
                <option value="pending">
                  {{ t('AI_BOT.KNOWLEDGE.PENDING') }}
                </option>
              </select>
              <textarea
                v-model="newKnowledge.answer"
                :placeholder="t('AI_BOT.KNOWLEDGE.ANSWER')"
                :maxlength="limitFor('knowledge_answer_chars')"
                rows="3"
                class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
              />
              <div class="grid gap-2 text-xs text-n-slate-10 md:col-span-2 md:grid-cols-2">
                <span>{{ limitCounter(newKnowledge.question, 'knowledge_question_chars') }}</span>
                <span>{{ limitCounter(newKnowledge.answer, 'knowledge_answer_chars') }}</span>
              </div>
              <div class="flex justify-end md:col-span-2">
                <Button
                  icon="i-lucide-plus"
                  :label="t('AI_BOT.KNOWLEDGE.ADD')"
                  :is-loading="isKnowledgeLoading"
                  :disabled="
                    !selectedAssistantId ||
                    hasReachedLimit('knowledge_entries', 'max_knowledge_entries')
                  "
                  @click="createKnowledge"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <div
                v-for="entry in knowledgeEntries"
                :key="entry.id"
                class="rounded-lg border border-n-weak bg-n-background p-4"
              >
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div class="min-w-0">
                    <div class="text-sm font-medium text-n-slate-12">
                      {{ entry.question }}
                    </div>
                    <div class="mt-2 whitespace-pre-wrap text-sm text-n-slate-11">
                      {{ entry.answer }}
                    </div>
                  </div>
                  <span class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11">
                    {{ t(STATUS_LABELS[entry.status] || 'AI_BOT.KNOWLEDGE.PENDING') }}
                  </span>
                </div>
                <div class="mt-4 flex flex-wrap justify-end gap-2">
                  <Button
                    v-if="entry.status !== 'approved'"
                    icon="i-lucide-check"
                    xs
                    faded
                    :label="t('AI_BOT.KNOWLEDGE.APPROVE')"
                    @click="approveKnowledge(entry)"
                  />
                  <Button
                    v-if="entry.status !== 'rejected'"
                    icon="i-lucide-ban"
                    xs
                    faded
                    :label="t('AI_BOT.KNOWLEDGE.REJECT')"
                    @click="rejectKnowledge(entry)"
                  />
                  <Button
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    :label="t('AI_BOT.KNOWLEDGE.DELETE')"
                    @click="deleteKnowledge(entry)"
                  />
                </div>
              </div>
              <div
                v-if="!knowledgeEntries.length"
                class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
              >
                {{ t('AI_BOT.KNOWLEDGE.EMPTY') }}
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'documents'"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-5"
          >
            <div class="mb-5 flex flex-wrap items-center justify-between gap-3 text-xs text-n-slate-10">
              <span>{{ usageCounter('documents', 'max_documents') }}</span>
              <span>{{ documentFetchLimitLabel }}</span>
              <span>{{ documentStorageLabel }}</span>
            </div>
            <div class="mb-5 grid gap-3 md:grid-cols-[minmax(0,1fr)_10rem]">
              <input
                v-model="newDocument.name"
                :placeholder="t('AI_BOT.DOCUMENTS.NAME')"
                :maxlength="limitFor('document_name_chars')"
                class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
              />
              <select
                v-model="newDocument.source_type"
                class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
              >
                <option value="text">{{ t('AI_BOT.DOCUMENTS.TEXT') }}</option>
                <option value="url">{{ t('AI_BOT.DOCUMENTS.URL') }}</option>
                <option value="pdf">{{ t('AI_BOT.DOCUMENTS.PDF') }}</option>
              </select>
              <input
                v-if="newDocument.source_type !== 'text'"
                v-model="newDocument.source_url"
                :placeholder="t('AI_BOT.DOCUMENTS.SOURCE_URL')"
                :maxlength="limitFor('document_url_chars')"
                class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
              />
              <textarea
                v-else
                v-model="newDocument.content"
                :placeholder="t('AI_BOT.DOCUMENTS.CONTENT')"
                :maxlength="limitFor('max_document_chars')"
                rows="4"
                class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
              />
              <div class="text-xs text-n-slate-10 md:col-span-2">
                {{
                  newDocument.source_type === 'text'
                    ? limitCounter(newDocument.content, 'max_document_chars')
                    : limitCounter(newDocument.source_url, 'document_url_chars')
                }}
              </div>
              <label class="flex items-center gap-3 text-sm text-n-slate-12">
                <input v-model="newDocument.process_now" type="checkbox" />
                <span>{{ t('AI_BOT.DOCUMENTS.PROCESS_NOW') }}</span>
              </label>
              <div class="flex justify-end">
                <Button
                  icon="i-lucide-file-plus"
                  :label="t('AI_BOT.DOCUMENTS.ADD')"
                  :is-loading="isDocumentLoading"
                  :disabled="
                    !selectedAssistantId ||
                    hasReachedLimit('documents', 'max_documents')
                  "
                  @click="createDocument"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <Button
                class="self-start"
                icon="i-lucide-refresh-cw"
                faded
                :label="t('AI_BOT.DOCUMENTS.REFRESH')"
                :is-loading="isDocumentLoading"
                @click="fetchDocuments(selectedAssistantId)"
              />
              <div
                v-for="document in documents"
                :key="document.id"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-background p-4"
              >
                <div class="min-w-0">
                  <div class="truncate text-sm font-medium text-n-slate-12">
                    {{ document.name }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ document.source_type }} ·
                    {{ t(DOCUMENT_STATUS_LABELS[document.status] || 'AI_BOT.DOCUMENTS.DRAFT') }} ·
                    {{ document.knowledge_entries_count }}
                  </div>
                  <div
                    v-if="document.last_error"
                    class="mt-1 break-words text-xs text-n-ruby-9"
                  >
                    {{ document.last_error }}
                  </div>
                  <details
                    v-if="document.status === 'ready'"
                    class="mt-2 text-sm text-n-slate-11"
                  >
                    <summary class="cursor-pointer">
                      {{ t('AI_BOT.DOCUMENTS.PREVIEW') }}
                    </summary>
                    <p class="mt-2">
                      {{ t('AI_BOT.DOCUMENTS.VERIFIED', {
                        chars: document.metadata?.extracted_chars || 0,
                        chunks: document.knowledge_entries_count,
                        embedded: document.embedded_entries_count,
                      }) }}
                    </p>
                    <p v-if="document.embedding_failed_entries_count" class="text-n-ruby-9">
                      {{ t('AI_BOT.DOCUMENTS.EMBEDDING_FAILED', { count: document.embedding_failed_entries_count }) }}
                    </p>
                    <p v-if="document.metadata?.source_title" class="font-medium">
                      {{ document.metadata.source_title }}
                    </p>
                    <pre class="max-h-64 overflow-y-auto whitespace-pre-wrap break-words font-sans">{{ document.content_preview }}</pre>
                  </details>
                </div>
                <div class="flex flex-wrap gap-2">
                  <Button
                    icon="i-lucide-refresh-cw"
                    xs
                    faded
                    :label="t('AI_BOT.DOCUMENTS.PROCESS')"
                    :disabled="isDocumentLoading || ['queued', 'processing'].includes(document.status)"
                    @click="processDocument(document)"
                  />
                  <Button
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    :label="t('AI_BOT.DOCUMENTS.DELETE')"
                    @click="deleteDocument(document)"
                  />
                </div>
              </div>
              <div
                v-if="!documents.length"
                class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
              >
                {{ t('AI_BOT.DOCUMENTS.EMPTY') }}
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'inboxes'"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-5"
          >
            <div class="mb-5 flex flex-col gap-3 md:flex-row">
              <select
                v-model="selectedInboxId"
                class="h-10 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
              >
                <option value="">
                  {{
                    availableInboxes.length
                      ? t('AI_BOT.INBOXES.SELECT')
                      : t('AI_BOT.INBOXES.NO_AVAILABLE')
                  }}
                </option>
                <option
                  v-for="inbox in availableInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }} · {{ inbox.channel_type }}
                </option>
              </select>
              <Button
                icon="i-lucide-link"
                :label="t('AI_BOT.INBOXES.LINK')"
                :is-loading="isInboxLoading"
                :disabled="!selectedInboxId"
                @click="linkInbox"
              />
            </div>

            <div class="flex flex-col gap-3">
              <div
                v-for="link in inboxLinks"
                :key="link.id"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-background p-4"
              >
                <div class="min-w-0">
                  <div class="truncate text-sm font-medium text-n-slate-12">
                    {{ link.inbox?.name || `#${link.inbox_id}` }}
                  </div>
                  <div class="text-xs text-n-slate-10">
                    {{ link.inbox?.channel_type || 'Inbox' }} · {{ link.status }}
                  </div>
                </div>
                <div class="flex flex-wrap gap-2">
                  <Button
                    icon="i-lucide-power"
                    xs
                    faded
                    :label="
                      link.status === 'active'
                        ? t('AI_BOT.INBOXES.DEACTIVATE')
                        : t('AI_BOT.INBOXES.ACTIVATE')
                    "
                    @click="toggleInboxLink(link)"
                  />
                  <Button
                    icon="i-lucide-unlink"
                    xs
                    ruby
                    faded
                    :label="t('AI_BOT.INBOXES.UNLINK')"
                    @click="unlinkInbox(link)"
                  />
                </div>
              </div>
              <div
                v-if="!inboxLinks.length"
                class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
              >
                {{ t('AI_BOT.INBOXES.EMPTY') }}
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'tools'"
            class="rounded-lg border border-n-weak bg-n-solid-2 p-5"
          >
            <div class="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div class="text-xs text-n-slate-10">
                {{ usageCounterForValue(selectedAssistantToolCount, 'max_tools') }}
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-md bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-3"
                  @click="applyToolExample('order')"
                >
                  {{ t('AI_BOT.EXAMPLES.TOOL_ORDER') }}
                </button>
                <button
                  type="button"
                  class="rounded-md bg-n-alpha-2 px-3 py-1.5 text-xs text-n-slate-11 hover:bg-n-alpha-3"
                  @click="applyToolExample('booking')"
                >
                  {{ t('AI_BOT.EXAMPLES.TOOL_BOOKING') }}
                </button>
              </div>
            </div>
            <div class="grid gap-5 xl:grid-cols-[16rem_minmax(0,1fr)]">
              <aside class="flex flex-col gap-2">
                <button
                  type="button"
                  class="rounded-lg border border-dashed border-n-weak px-4 py-3 text-left text-sm text-n-slate-11"
                  @click="resetToolForm()"
                >
                  {{ t('AI_BOT.TOOLS.NEW') }}
                </button>
                <button
                  v-for="tool in customTools"
                  :key="tool.id"
                  type="button"
                  class="rounded-lg border px-4 py-3 text-left text-sm"
                  :class="
                    tool.id === selectedToolId
                      ? 'border-n-blue-7 bg-n-blue-2 text-n-slate-12'
                      : 'border-n-weak bg-n-background text-n-slate-11'
                  "
                  @click="resetToolForm(tool)"
                >
                  <span class="block truncate font-medium">{{ tool.name }}</span>
                  <span class="block truncate text-xs">{{ tool.slug }}</span>
                </button>
              </aside>

              <div class="grid min-w-0 gap-3 md:grid-cols-2">
                <input
                  v-model="toolForm.name"
                  :placeholder="t('AI_BOT.TOOLS.NAME')"
                  :maxlength="limitFor('tool_name_chars')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <input
                  v-model="toolForm.slug"
                  :placeholder="t('AI_BOT.TOOLS.SLUG')"
                  :maxlength="limitFor('tool_slug_chars')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <select
                  v-model="toolForm.http_method"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                >
                  <option value="get">GET</option>
                  <option value="post">POST</option>
                </select>
                <input
                  v-model="toolForm.endpoint_url"
                  :placeholder="t('AI_BOT.TOOLS.ENDPOINT')"
                  :maxlength="limitFor('document_url_chars')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <textarea
                  v-model="toolForm.description"
                  :placeholder="t('AI_BOT.TOOLS.DESCRIPTION')"
                  :maxlength="limitFor('tool_description_chars')"
                  rows="3"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
                />
                <div
                  class="flex min-w-0 flex-col gap-3 md:col-span-2"
                  data-testid="ai-bot-tool-parameters"
                >
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('AI_BOT.TOOLS.PARAMETERS') }}</span>
                    <Button
                      icon="i-lucide-plus"
                      :label="t('AI_BOT.TOOLS.ADD_PARAMETER')"
                      faded
                      @click="addToolParameter"
                    />
                  </div>
                  <fieldset
                    v-for="(parameter, index) in toolParameters"
                    :key="index"
                    class="min-w-0 rounded-lg border border-n-weak bg-n-background p-3"
                    data-testid="ai-bot-tool-parameter"
                  >
                    <legend class="px-1 text-xs font-medium text-n-slate-11">
                      {{ t('AI_BOT.TOOLS.PARAMETERS') }} {{ index + 1 }}
                    </legend>
                    <div class="grid min-w-0 gap-3 sm:grid-cols-2">
                      <label class="flex min-w-0 flex-col gap-1.5 text-xs text-n-slate-11">
                        <span>{{ t('AI_BOT.TOOLS.PARAMETER_NAME') }}</span>
                        <input
                          v-model="parameter.name"
                          :placeholder="t('AI_BOT.TOOLS.PARAMETER_NAME')"
                          :maxlength="limitFor('tool_parameter_name_chars')"
                          class="h-10 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
                        />
                      </label>
                      <label class="flex min-w-0 flex-col gap-1.5 text-xs text-n-slate-11">
                        <span>{{ t('AI_BOT.TOOLS.PARAMETER_SOURCE') }}</span>
                        <select
                          v-model="parameter.source"
                          class="h-10 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
                        >
                          <option value="model">{{ t('AI_BOT.TOOLS.SOURCE_MODEL') }}</option>
                          <option value="contact_id">{{ t('AI_BOT.TOOLS.SOURCE_CONTACT_ID') }}</option>
                          <option value="contact_email">{{ t('AI_BOT.TOOLS.SOURCE_CONTACT_EMAIL') }}</option>
                          <option value="contact_phone">{{ t('AI_BOT.TOOLS.SOURCE_CONTACT_PHONE') }}</option>
                          <option value="conversation_id">{{ t('AI_BOT.TOOLS.SOURCE_CONVERSATION_ID') }}</option>
                          <option value="conversation_display_id">{{ t('AI_BOT.TOOLS.SOURCE_CONVERSATION_DISPLAY_ID') }}</option>
                          <option value="inbox_id">{{ t('AI_BOT.TOOLS.SOURCE_INBOX_ID') }}</option>
                          <option value="account_id">{{ t('AI_BOT.TOOLS.SOURCE_ACCOUNT_ID') }}</option>
                        </select>
                      </label>
                      <label class="flex min-w-0 flex-col gap-1.5 text-xs text-n-slate-11">
                        <span>{{ t('AI_BOT.TOOLS.OWNERSHIP_RESPONSE_FIELD') }}</span>
                        <input
                          v-model="parameter.response_field"
                          :disabled="!contactIdentitySources.includes(parameter.source)"
                          :placeholder="t('AI_BOT.TOOLS.OWNERSHIP_RESPONSE_FIELD')"
                          class="h-10 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-50 focus:border-n-blue-8"
                        />
                      </label>
                      <label class="flex min-w-0 flex-col gap-1.5 text-xs text-n-slate-11">
                        <span>{{ t('AI_BOT.TOOLS.PARAMETER_TYPE') }}</span>
                        <select
                          v-model="parameter.type"
                          class="h-10 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
                        >
                          <option value="string">String</option>
                          <option value="integer">Integer</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                        </select>
                      </label>
                      <label class="flex min-w-0 flex-col gap-1.5 text-xs text-n-slate-11 sm:col-span-2">
                        <span>{{ t('AI_BOT.TOOLS.PARAMETER_DESCRIPTION') }}</span>
                        <input
                          v-model="parameter.description"
                          :placeholder="t('AI_BOT.TOOLS.PARAMETER_DESCRIPTION')"
                          :maxlength="limitFor('tool_parameter_description_chars')"
                          class="h-10 w-full min-w-0 rounded-lg border border-n-weak bg-n-background px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8"
                        />
                      </label>
                      <div class="flex min-w-0 justify-end sm:col-span-2">
                        <Button
                          class="w-full sm:w-auto"
                          icon="i-lucide-trash-2"
                          :label="t('AI_BOT.TOOLS.REMOVE_PARAMETER')"
                          faded
                          ruby
                          @click="removeToolParameter(index)"
                        />
                      </div>
                    </div>
                  </fieldset>
                  <span class="text-xs text-n-slate-10">{{ lineCounter(toolParameters.map(parameter => parameter.name).join('\n'), 'tool_parameters') }}</span>
                </div>
                <textarea
                  v-model="responseFieldsText"
                  :placeholder="t('AI_BOT.TOOLS.RESPONSE_FIELDS')"
                  rows="3"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
                />
                <div class="grid gap-2 text-xs text-n-slate-10 md:col-span-2 md:grid-cols-2">
                  <span>{{ limitCounter(toolForm.description, 'tool_description_chars') }}</span>
                  <span>{{ lineCounter(responseFieldsText, 'tool_parameters') }}</span>
                </div>
                <div class="md:col-span-2">
                  <span
                    class="inline-flex rounded-md px-2 py-1 text-xs font-medium"
                    :class="toolRunsAutomatically ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-amber-3 text-n-amber-11'"
                  >
                    {{ toolRunsAutomatically ? t('AI_BOT.TOOLS.AUTOMATIC') : t('AI_BOT.TOOLS.MANUAL_APPROVAL') }}
                  </span>
                </div>
                <select
                  v-model="toolForm.auth_config.type"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                >
                  <option value="none">{{ t('AI_BOT.TOOLS.AUTH_NONE') }}</option>
                  <option value="bearer">{{ t('AI_BOT.TOOLS.AUTH_BEARER') }}</option>
                  <option value="header">{{ t('AI_BOT.TOOLS.AUTH_HEADER') }}</option>
                  <option value="basic">{{ t('AI_BOT.TOOLS.AUTH_BASIC') }}</option>
                </select>
                <input
                  v-if="toolForm.auth_config.type === 'header'"
                  v-model="toolForm.auth_config.header_name"
                  :placeholder="t('AI_BOT.TOOLS.HEADER_NAME')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <input
                  v-if="['bearer', 'header'].includes(toolForm.auth_config.type)"
                  v-model="toolForm.auth_config.token"
                  :placeholder="t('AI_BOT.TOOLS.TOKEN')"
                  type="password"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <input
                  v-if="toolForm.auth_config.type === 'basic'"
                  v-model="toolForm.auth_config.username"
                  :placeholder="t('AI_BOT.TOOLS.USERNAME')"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <input
                  v-if="toolForm.auth_config.type === 'basic'"
                  v-model="toolForm.auth_config.password"
                  :placeholder="t('AI_BOT.TOOLS.PASSWORD')"
                  type="password"
                  class="h-10 rounded-lg border border-n-weak bg-n-background px-3 text-sm outline-none focus:border-n-blue-8"
                />
                <textarea
                  v-model="toolForm.request_template"
                  :placeholder="t('AI_BOT.TOOLS.REQUEST_TEMPLATE')"
                  :maxlength="limitFor('tool_template_chars')"
                  rows="3"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
                />
                <textarea
                  v-model="toolForm.response_template"
                  :placeholder="t('AI_BOT.TOOLS.RESPONSE_TEMPLATE')"
                  :maxlength="limitFor('tool_response_template_chars')"
                  rows="3"
                  class="rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm outline-none focus:border-n-blue-8 md:col-span-2"
                />
                <div class="grid gap-2 text-xs text-n-slate-10 md:col-span-2 md:grid-cols-2">
                  <span>{{ limitCounter(toolForm.request_template, 'tool_template_chars') }}</span>
                  <span>{{ limitCounter(toolForm.response_template, 'tool_response_template_chars') }}</span>
                </div>
                <label class="flex items-center gap-3 text-sm text-n-slate-12">
                  <input v-model="toolForm.enabled" type="checkbox" />
                  <span>{{ t('AI_BOT.TOOLS.ENABLED') }}</span>
                </label>
                <div class="flex flex-wrap justify-end gap-2">
                  <Button
                    v-if="selectedTool"
                    icon="i-lucide-trash-2"
                    ruby
                    faded
                    :label="t('AI_BOT.TOOLS.DELETE')"
                    @click="deleteTool"
                  />
                  <Button
                    icon="i-lucide-save"
                    :label="t('AI_BOT.TOOLS.SAVE')"
                    :is-loading="isToolLoading"
                    :disabled="!selectedToolId && selectedAssistantToolLimitReached"
                    @click="saveTool"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-else-if="activeTab === 'usage'"
            class="flex flex-col gap-8"
          >
            <div
              v-if="creditPaymentNotice"
              role="status"
              class="flex min-h-11 items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-2 px-4 py-3 text-sm text-n-slate-11"
            >
              <span class="i-lucide-circle-check size-4 flex-shrink-0" />
              <span>{{ creditPaymentNotice }}</span>
            </div>

            <section aria-labelledby="ai-credit-heading">
              <div class="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2
                    id="ai-credit-heading"
                    class="text-sm font-medium text-n-slate-12"
                  >
                    {{ t('AI_BOT.CREDITS.TITLE') }}
                  </h2>
                  <p class="mt-1 max-w-2xl text-xs text-n-slate-10">
                    {{ t('AI_BOT.CREDITS.DESCRIPTION') }}
                  </p>
                </div>
                <Button
                  icon="i-lucide-refresh-cw"
                  faded
                  :label="t('AI_BOT.CREDITS.REFRESH')"
                  :is-loading="isCreditLoading"
                  @click="fetchCreditManagement"
                />
              </div>

              <div
                v-if="isCreditLoading && !creditManagement"
                role="status"
                class="rounded-lg border border-dashed border-n-weak px-4 py-8 text-center text-sm text-n-slate-10"
              >
                {{ t('AI_BOT.CREDITS.LOADING') }}
              </div>
              <div
                v-else-if="creditError"
                role="alert"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-ruby-6 bg-n-ruby-2 px-4 py-3 text-sm text-n-ruby-11"
              >
                <span>{{ t('AI_BOT.CREDITS.LOAD_ERROR') }}</span>
                <Button
                  icon="i-lucide-refresh-cw"
                  ruby
                  faded
                  :label="t('AI_BOT.CREDITS.RETRY')"
                  @click="fetchCreditManagement"
                />
              </div>

              <template v-else-if="creditManagement">
                <div
                  v-if="creditIsUnlimited"
                  class="mb-5 flex items-start gap-3 rounded-lg border border-n-teal-6 bg-n-teal-2 px-4 py-3 text-sm text-n-teal-11"
                >
                  <span class="i-lucide-infinity mt-0.5 size-4 flex-shrink-0" />
                  <div>
                    <div class="font-medium">
                      {{ t('AI_BOT.CREDITS.UNLIMITED_TITLE') }}
                    </div>
                    <div class="mt-1 text-xs">
                      {{ t('AI_BOT.CREDITS.UNLIMITED_DESCRIPTION') }}
                    </div>
                  </div>
                </div>
                <div
                  v-else-if="creditSnapshot.exceeded"
                  role="alert"
                  class="mb-5 flex items-start gap-3 rounded-lg border border-n-amber-6 bg-n-amber-2 px-4 py-3 text-sm text-n-amber-11"
                >
                  <span class="i-lucide-triangle-alert mt-0.5 size-4 flex-shrink-0" />
                  <span>{{ t('AI_BOT.CREDITS.EXCEEDED') }}</span>
                </div>

                <p class="mb-5 flex items-start gap-2 text-xs text-n-slate-10">
                  <span class="i-lucide-calendar-clock mt-0.5 size-4 flex-shrink-0" />
                  <span>{{ t('AI_BOT.CREDITS.PERIOD') }}</span>
                </p>

                <dl class="grid overflow-hidden rounded-lg border border-n-weak bg-n-background sm:grid-cols-2 xl:grid-cols-4">
                  <div class="border-b border-n-weak p-4 sm:border-r xl:border-b-0">
                    <dt class="text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.BASE_LIMIT') }}
                    </dt>
                    <dd class="mt-1 text-lg font-semibold text-n-slate-12">
                      {{ creditLimitLabel(creditSnapshot.baseMonthlyLimitCents) }}
                    </dd>
                    <dd v-if="!creditIsUnlimited" class="mt-1 text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.BASE_DETAIL', {
                        remaining: formatMoneyMicros(creditSnapshot.monthlyRemainingMicros),
                      }) }}
                    </dd>
                  </div>
                  <div class="border-b border-n-weak p-4 xl:border-b-0 xl:border-r">
                    <dt class="text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.WALLET_BALANCE') }}
                    </dt>
                    <dd class="mt-1 text-lg font-semibold text-n-slate-12">
                      {{ formatMoneyMicros(creditSnapshot.walletRemainingMicros) }}
                    </dd>
                    <dd class="mt-1 text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.WALLET_DETAIL', {
                        credit: formatMoneyCents(creditSnapshot.walletCreditCents),
                        spent: formatMoneyMicros(creditSnapshot.walletSpentMicros),
                      }) }}
                    </dd>
                  </div>
                  <div class="border-b border-n-weak p-4 sm:border-r xl:border-b-0">
                    <dt class="text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.SPENT') }}
                    </dt>
                    <dd class="mt-1 text-lg font-semibold text-n-slate-12">
                      {{ formatMoneyMicros(creditSnapshot.monthlySpentMicros) }}
                    </dd>
                  </div>
                  <div class="p-4">
                    <dt class="text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.REMAINING') }}
                    </dt>
                    <dd class="mt-1 text-lg font-semibold text-n-slate-12">
                      {{ creditRemainingLabel }}
                    </dd>
                  </div>
                </dl>

                <div v-if="!creditIsUnlimited" class="mt-7">
                  <div class="mb-4">
                    <h3 class="text-sm font-medium text-n-slate-12">
                      {{ t('AI_BOT.CREDITS.TOP_UP.TITLE') }}
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.TOP_UP.DESCRIPTION', { currency: paymentCurrency }) }}
                    </p>
                  </div>
                  <div
                    class="grid max-w-2xl items-end gap-3 sm:grid-cols-[minmax(0,1fr)_auto]"
                  >
                    <label class="min-w-0 text-xs font-medium text-n-slate-11">
                      <span>{{ t('AI_BOT.CREDITS.TOP_UP.AMOUNT') }}</span>
                      <div class="mt-2 flex h-10 overflow-hidden rounded-md border border-n-weak bg-n-background focus-within:border-n-brand">
                        <input
                          v-model="topUpAmount"
                          class="min-w-0 flex-1 bg-transparent px-3 text-sm text-n-slate-12 outline-none disabled:cursor-not-allowed disabled:opacity-70"
                          dir="ltr"
                          inputmode="decimal"
                          :aria-invalid="Boolean(topUpAmount) && !topUpAmountValid"
                          :disabled="Boolean(pendingTopUpCents) || Boolean(checkoutAmountCents)"
                          :placeholder="t('AI_BOT.CREDITS.TOP_UP.PLACEHOLDER')"
                        />
                        <span class="flex min-w-14 items-center justify-center border-s border-n-weak px-3 text-xs font-medium text-n-slate-10">
                          {{ paymentCurrency }}
                        </span>
                      </div>
                    </label>
                    <Button
                      class="w-full sm:w-auto"
                      icon="i-lucide-credit-card"
                      :label="pendingTopUpCents ? t('AI_BOT.CREDITS.TOP_UP.CONTINUE') : t('AI_BOT.CREDITS.TOP_UP.PAY')"
                      :is-loading="Boolean(checkoutAmountCents)"
                      :disabled="!canStartTopUp"
                      @click="startCreditCheckout"
                    />
                  </div>
                  <p v-if="estimatedTopUpCreditCents" class="mt-3 text-xs text-n-slate-10">
                    {{ t('AI_BOT.CREDITS.TOP_UP.ESTIMATE', { credit: formatMoneyCents(estimatedTopUpCreditCents) }) }}
                  </p>
                  <p v-if="pendingTopUpCents" class="mt-2 text-xs text-n-amber-11" role="status">
                    {{ t('AI_BOT.CREDITS.TOP_UP.PENDING', { amount: formatMoneyCents(pendingTopUpCents, paymentCurrency) }) }}
                  </p>
                  <p v-else-if="topUpAmount && !topUpAmountValid" class="mt-2 text-xs text-n-ruby-11" role="alert">
                    {{ t('AI_BOT.CREDITS.TOP_UP.RANGE', {
                      minimum: formatMoneyCents(creditManagement.min_top_up_cents, paymentCurrency),
                      maximum: formatMoneyCents(creditManagement.max_top_up_cents, paymentCurrency),
                    }) }}
                  </p>
                  <p
                    v-if="creditManagement.action?.available !== true"
                    role="status"
                    class="mt-3 text-xs text-n-slate-10"
                  >
                    {{ creditActionMessage }}
                  </p>
                  <p
                    v-if="creditCheckoutError"
                    role="alert"
                    class="mt-3 text-sm text-n-ruby-11"
                  >
                    {{ t('AI_BOT.CREDITS.CHECKOUT_ERROR') }}
                  </p>
                </div>

                <div class="mt-8 border-t border-n-weak pt-6">
                  <div class="mb-4">
                    <h3 class="text-sm font-medium text-n-slate-12">
                      {{ t('AI_BOT.CREDITS.PURCHASES.TITLE') }}
                    </h3>
                    <p class="mt-1 text-xs text-n-slate-10">
                      {{ t('AI_BOT.CREDITS.PURCHASES.DESCRIPTION') }}
                    </p>
                  </div>
                  <div class="flex flex-col gap-2">
                    <article
                      v-for="purchase in creditPurchases"
                      :key="purchase.id"
                      class="grid gap-3 rounded-lg border border-n-weak bg-n-background px-4 py-3 text-sm sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_repeat(4,minmax(7rem,auto))]"
                    >
                      <div class="min-w-0">
                        <div class="truncate font-medium text-n-slate-12">
                          {{ purchase.id }}
                        </div>
                        <div class="text-xs text-n-slate-10">
                          {{ creditPurchaseStatus(purchase.status) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-n-slate-10">
                          {{ t('AI_BOT.CREDITS.PURCHASES.CREDIT') }}
                        </div>
                        <div class="text-n-slate-12">
                          {{ formatMoneyCents(purchase.credit_cents) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-n-slate-10">
                          {{ t('AI_BOT.CREDITS.PURCHASES.PRICE') }}
                        </div>
                        <div class="text-n-slate-12">
                          {{ formatMoneyCents(purchase.price_cents, purchase.currency) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-n-slate-10">
                          {{ t('AI_BOT.CREDITS.PURCHASES.CREATED_AT') }}
                        </div>
                        <div class="text-xs text-n-slate-12">
                          {{ formatCreditDate(purchase.created_at) }}
                        </div>
                      </div>
                      <div>
                        <div class="text-xs text-n-slate-10">
                          {{ t('AI_BOT.CREDITS.PURCHASES.PAID_AT') }}
                        </div>
                        <div class="text-xs text-n-slate-12">
                          {{ formatCreditDate(purchase.paid_at) }}
                        </div>
                      </div>
                    </article>
                    <div
                      v-if="!creditPurchases.length"
                      class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-10"
                    >
                      {{ t('AI_BOT.CREDITS.PURCHASES.EMPTY') }}
                    </div>
                  </div>
                  <div
                    v-if="creditPurchasePagination.total > creditPurchasePagination.page_size"
                    class="mt-4 flex flex-wrap items-center justify-end gap-3 text-xs text-n-slate-10"
                  >
                    <button
                      type="button"
                      class="min-h-9 rounded-md border border-n-weak px-3 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="creditPage <= 1 || isCreditLoading"
                      @click="changeCreditPage(-1)"
                    >
                      {{ t('AI_BOT.PAGINATION.PREVIOUS') }}
                    </button>
                    <span>{{ t('AI_BOT.PAGINATION.PAGE', { page: creditPage, pages: creditTotalPages }) }}</span>
                    <button
                      type="button"
                      class="min-h-9 rounded-md border border-n-weak px-3 disabled:cursor-not-allowed disabled:opacity-50"
                      :disabled="creditPage >= creditTotalPages || isCreditLoading"
                      @click="changeCreditPage(1)"
                    >
                      {{ t('AI_BOT.PAGINATION.NEXT') }}
                    </button>
                  </div>
                </div>
              </template>
            </section>

            <div class="mb-5 flex flex-wrap items-end justify-between gap-3 border-t border-n-weak pt-7">
              <div>
                <h2 id="ai-usage-heading" class="text-sm font-medium text-n-slate-12">
                  {{ t('AI_BOT.USAGE.TITLE') }}
                </h2>
                <div class="text-xs text-n-slate-10">
                  {{ budgetText(activeBudget) }}
                </div>
              </div>
              <div class="flex flex-wrap items-end gap-3">
                <div class="flex min-w-44 flex-col gap-1 text-xs text-n-slate-10">
                  <span>{{ t('AI_BOT.USAGE.ASSISTANT_FILTER') }}</span>
                  <ComboBox
                    :model-value="usageAssistantId"
                    :options="usageAssistantOptions"
                    :placeholder="t('AI_BOT.USAGE.ALL_ASSISTANTS')"
                    @update:model-value="changeUsageAssistant"
                  />
                </div>
                <div class="flex min-w-36 flex-col gap-1 text-xs text-n-slate-10">
                  <span>{{ t('AI_BOT.USAGE.PERIOD') }}</span>
                  <ComboBox
                    :model-value="usageDays"
                    :options="usagePeriodOptions"
                    :placeholder="t('AI_BOT.USAGE.PERIOD')"
                    @update:model-value="changeUsagePeriod"
                  />
                </div>
                <Button
                  icon="i-lucide-refresh-cw"
                  :label="t('AI_BOT.USAGE.REFRESH')"
                  :is-loading="isUsageLoading"
                  @click="refreshUsageTab"
                />
              </div>
            </div>
            <div
              v-if="usageSummary"
              class="grid gap-3 md:grid-cols-4"
            >
              <div class="rounded-lg border border-n-weak bg-n-background p-4">
                <div class="text-xs text-n-slate-10">
                  {{ t('AI_BOT.USAGE.TOTAL') }}
                </div>
                <div class="text-xl font-semibold text-n-slate-12">
                  {{ formatNumber(usageSummary.total) }}
                </div>
              </div>
              <div class="rounded-lg border border-n-weak bg-n-background p-4">
                <div class="text-xs text-n-slate-10">
                  {{ t('AI_BOT.USAGE.COST') }}
                </div>
                <div class="text-xl font-semibold text-n-slate-12">
                  {{ formatUsdMicros(usageSummary.cost_micros) }}
                </div>
              </div>
              <div class="rounded-lg border border-n-weak bg-n-background p-4">
                <div class="text-xs text-n-slate-10">
                  {{ t('AI_BOT.USAGE.PROMPT_TOKENS') }}
                </div>
                <div class="text-xl font-semibold text-n-slate-12">
                  {{ formatNumber(usageSummary.prompt_tokens) }}
                </div>
              </div>
              <div class="rounded-lg border border-n-weak bg-n-background p-4">
                <div class="text-xs text-n-slate-10">
                  {{ t('AI_BOT.USAGE.COMPLETION_TOKENS') }}
                </div>
                <div class="text-xl font-semibold text-n-slate-12">
                  {{ formatNumber(usageSummary.completion_tokens) }}
                </div>
              </div>
            </div>
            <dl
              v-if="usageSummary?.current_account_budget && !activeBudgetSnapshot.unlimited"
              class="mt-5 grid overflow-hidden rounded-lg border border-n-weak bg-n-background sm:grid-cols-3"
            >
              <div class="border-b border-n-weak p-3 sm:border-b-0 sm:border-r">
                <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.USAGE.PLAN_REMAINING') }}</dt>
                <dd class="mt-1 font-medium text-n-slate-12">
                  {{ formatUsdMicros(activeBudgetSnapshot.monthlyRemainingMicros) }}
                </dd>
              </div>
              <div class="border-b border-n-weak p-3 sm:border-b-0 sm:border-r">
                <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.USAGE.WALLET_REMAINING') }}</dt>
                <dd class="mt-1 font-medium text-n-slate-12">
                  {{ formatUsdMicros(activeBudgetSnapshot.walletRemainingMicros) }}
                </dd>
              </div>
              <div class="p-3">
                <dt class="text-xs text-n-slate-10">{{ t('AI_BOT.USAGE.TOTAL_REMAINING') }}</dt>
                <dd class="mt-1 font-medium text-n-slate-12">
                  {{ formatUsdMicros(activeBudgetSnapshot.remainingMicros) }}
                </dd>
              </div>
            </dl>
            <div v-if="usageSummary" class="mt-5 flex flex-wrap gap-2">
              <span
                v-for="[eventType, count] in Object.entries(usageSummary.by_event_type || {})"
                :key="eventType"
                class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
              >
                {{ usageEventLabel(eventType) }}: {{ count }}
              </span>
              <span
                v-for="[eventType, cost] in Object.entries(usageSummary.by_event_cost || {})"
                :key="`${eventType}-cost`"
                class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
              >
                {{ usageEventLabel(eventType) }}: {{ cost }}
              </span>
            </div>
            <div class="mt-5 flex flex-col gap-2">
              <div
                v-for="event in usageSummary?.recent || []"
                :key="event.id"
                class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-background px-4 py-3 text-sm text-n-slate-11"
              >
                <span class="min-w-0">
                  <span class="block font-medium text-n-slate-12">
                    {{ usageEventLabel(event.event_type) }}
                  </span>
                  <span class="block text-xs text-n-slate-10">
                    {{ t('AI_BOT.USAGE.PROMPT_TOKENS') }}:
                    {{ formatNumber(event.prompt_tokens) }} ·
                    {{ t('AI_BOT.USAGE.COMPLETION_TOKENS') }}:
                    {{ formatNumber(event.completion_tokens) }}
                  </span>
                </span>
                <span class="text-right">
                  <span class="block font-medium text-n-slate-12">
                    {{ formatUsdMicros(event.cost_micros) }}
                  </span>
                  <span class="block text-xs text-n-slate-10">
                    {{ formatUsageDate(event.created_at) }}
                  </span>
                </span>
              </div>
              <div
                v-if="usageSummary && !(usageSummary.recent || []).length"
                class="rounded-lg border border-dashed border-n-weak px-4 py-6 text-sm text-n-slate-11"
              >
                {{ t('AI_BOT.USAGE.EMPTY') }}
              </div>
              <div
                v-if="usageSummary && usagePagination.total > USAGE_PAGE_SIZE"
                class="mt-3 flex flex-wrap items-center justify-end gap-3 text-xs text-n-slate-10"
              >
                <button
                  type="button"
                  class="rounded-md border border-n-weak px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="usagePage <= 1 || isUsageLoading"
                  @click="changeUsagePage(-1)"
                >
                  {{ t('AI_BOT.PAGINATION.PREVIOUS') }}
                </button>
                <span>{{ t('AI_BOT.PAGINATION.PAGE', { page: usagePage, pages: usageTotalPages }) }}</span>
                <button
                  type="button"
                  class="rounded-md border border-n-weak px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                  :disabled="usagePage >= usageTotalPages || isUsageLoading"
                  @click="changeUsagePage(1)"
                >
                  {{ t('AI_BOT.PAGINATION.NEXT') }}
                </button>
              </div>
            </div>
          </div>

        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
